import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { devicesApi } from "./storageRoles/devicesSharedApi";
import { storageDevicesApi } from "./storageRoles/devicesApi";
import { firmLogApi } from "./storageRoles/firmLogApi";
import { auditApi } from "./storageRoles/auditApi";
import { developmentApi } from "./storageRoles/developmentApi";
import { tasksApi } from "./storageRoles/tasksApi";
import { notificationsApi } from "./storageRoles/notificationsApi";
import { devicesSettingsV2Api } from "./storageRoles/devicesSettingsV2Api";

import { usersApi } from "./storageRoles/usersApi";
import { cityApi } from "./storageRoles/cityApi";
import { townApi } from "./storageRoles/townApi";
import { firmApi } from "./storageRoles/firmApi";
import { dealerFirmApi } from "./dealerRoles/firmApi";
import { productInfoApi } from "./storageRoles/productInfoApi";
import { productTypeApi } from "./storageRoles/productTypeApi";
import { authApi } from "./authApi";

import { BillsApi } from "./storageRoles/billsApi";
import { dealerBillsApi } from "./dealerRoles/billsApi";
import { dealerDevicesApi } from "./dealerRoles/devicesApi";
import { dealerUsersApi } from "./dealerRoles/usersApi";
import { DashBoardApi } from "./storageRoles/dashboardApi";
import { reportsApi } from "./storageRoles/reportsApi";
import { offerApi } from "./storageRoles/offerApi";
import { walletTopupApi } from "./storageRoles/walletTopupApi";
import { apkApi } from "./storageRoles/apkApi";
import { hardwareApi } from "./storageRoles/hardwareApi";
import { pushApi } from "./storageRoles/pushApi";
import { autoReportApi } from "./storageRoles/autoReportApi";
import { dealerAutoReportApi } from "./dealerRoles/autoReportApi";
import { pendingDevicesApi } from "./storageRoles/pendingDevicesApi";
import { pendingBootstrapApi } from "./storageRoles/pendingBootstrapApi";
import { emqxApi } from "./storageRoles/emqxApi";
import { factoryApi } from "./storageRoles/factoryApi";

/** 401 "Başka bir cihazdan giriş" veya 403 "Doğrulanmamış hesap" → oturumu kapat, login'e at */
const sessionExpiredMiddleware = () => (next) => (action) => {
  const result = next(action);
  if (action.type?.endsWith("/rejected")) {
    const err = action.payload?.error ?? action.payload;
    const status = err?.status;
    const message = (err?.data?.message ?? err?.message ?? "").toString();
    const reason = err?.data?.audit?.metadata?.reason;
    if (
      status === 401 &&
      (message.includes("Başka bir cihazdan") ||
        reason === "token_version_mismatch")
    ) {
      try {
        sessionStorage.setItem("logoutReason", "another_device");
      } catch (e) {}
      window.dispatchEvent(
        new CustomEvent("session-expired", { detail: { reason: "another_device" } })
      );
    }
    if (
      status === 403 &&
      (reason === "account_not_fully_verified" || message.includes("Doğrulanmamış hesap"))
    ) {
      try {
        sessionStorage.setItem("logoutReason", "account_not_verified");
      } catch (e) {}
      window.dispatchEvent(
        new CustomEvent("session-expired", { detail: { reason: "account_not_verified" } })
      );
    }
  }
  return result;
};

export const store = configureStore({
  reducer: {
    [devicesApi.reducerPath]: devicesApi.reducer,
    [storageDevicesApi.reducerPath]: storageDevicesApi.reducer,
    [firmApi.reducerPath]: firmApi.reducer,
    [dealerFirmApi.reducerPath]: dealerFirmApi.reducer,
    [firmLogApi.reducerPath]: firmLogApi.reducer,
    [auditApi.reducerPath]: auditApi.reducer,
    [developmentApi.reducerPath]: developmentApi.reducer,
    [tasksApi.reducerPath]: tasksApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [devicesSettingsV2Api.reducerPath]: devicesSettingsV2Api.reducer,
    [DashBoardApi.reducerPath]: DashBoardApi.reducer,
    [BillsApi.reducerPath]: BillsApi.reducer,
    [dealerBillsApi.reducerPath]: dealerBillsApi.reducer,
    [dealerDevicesApi.reducerPath]: dealerDevicesApi.reducer,
    [dealerUsersApi.reducerPath]: dealerUsersApi.reducer,
    [productInfoApi.reducerPath]: productInfoApi.reducer,
    [productTypeApi.reducerPath]: productTypeApi.reducer,
    [townApi.reducerPath]: townApi.reducer,
    [cityApi.reducerPath]: cityApi.reducer,
    [authApi.reducerPath]: authApi.reducer,

    [usersApi.reducerPath]: usersApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [offerApi.reducerPath]: offerApi.reducer,
    [walletTopupApi.reducerPath]: walletTopupApi.reducer,
    [apkApi.reducerPath]: apkApi.reducer,
    [hardwareApi.reducerPath]: hardwareApi.reducer,
    [pushApi.reducerPath]: pushApi.reducer,
    [autoReportApi.reducerPath]: autoReportApi.reducer,
    [dealerAutoReportApi.reducerPath]: dealerAutoReportApi.reducer,
    [pendingDevicesApi.reducerPath]: pendingDevicesApi.reducer,
    [pendingBootstrapApi.reducerPath]: pendingBootstrapApi.reducer,
    [emqxApi.reducerPath]: emqxApi.reducer,
    [factoryApi.reducerPath]: factoryApi.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware()
      .concat(sessionExpiredMiddleware)
      .concat(devicesApi.middleware)
      .concat(storageDevicesApi.middleware)
      .concat(firmApi.middleware)
      .concat(dealerFirmApi.middleware)
      .concat(firmLogApi.middleware)
      .concat(auditApi.middleware)
      .concat(developmentApi.middleware)
      .concat(tasksApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(devicesSettingsV2Api.middleware)
      .concat(DashBoardApi.middleware)
      .concat(BillsApi.middleware)
      .concat(dealerBillsApi.middleware)
      .concat(dealerDevicesApi.middleware)
      .concat(dealerUsersApi.middleware)
      .concat(productInfoApi.middleware)
      .concat(productTypeApi.middleware)
      .concat(townApi.middleware)
      .concat(cityApi.middleware)
      .concat(authApi.middleware)

      .concat(usersApi.middleware)
      .concat(reportsApi.middleware)
      .concat(offerApi.middleware)
      .concat(walletTopupApi.middleware)
      .concat(apkApi.middleware)
      .concat(hardwareApi.middleware)
      .concat(pushApi.middleware)
      .concat(autoReportApi.middleware)
      .concat(dealerAutoReportApi.middleware)
      .concat(pendingDevicesApi.middleware)
      .concat(pendingBootstrapApi.middleware)
      .concat(emqxApi.middleware)
      .concat(factoryApi.middleware);
  },
});

setupListeners(store.dispatch);

export {
  useAddFirmMutation,
  useGetFirmByIDQuery,
  useLazyGetFirmByIDQuery,
  useGetBelowFirmsByIDQuery,
  useUpdateFirmInfoMutation,
  useUpdateFirmSyncMutation,
  useUpdateTakeBackFirmProductQuotaMutation,
  useUpdateGiveFirmProductQuotaMutation,
  useSendSubFirmQuotaMutation,
  useLazyGetSendableSubFirmQuotaQuery,
  useDivideQuotaMutation,
  useDeleteFirmByIDMutation,
  useGetBelongUsersByFirmIDQuery,
  useGetDeviceByFirmIDQuery,
  useGetSyncByFirmIDQuery,
  useGetOtherFirmsQuery,
  useGetFirmProductsQuery,
  useUpdateFirmProductQuotaMutation,
  useGetCustomersByDealerFirmIdQuery,
  useUpdateFirmDealerRelationMutation,
  useGetRoleTemplatesQuery,
  useLazyGetRoleTemplatesQuery,
  useCreateRoleTemplateMutation,
  useUpdateRoleTemplateMutation,
  useDeleteRoleTemplateMutation,
  useGetTemplateAvailablePermissionsQuery,
  useSearchUnlinkedFirmsMutation,
  useLinkSubFirmMutation,
  useUnlinkSubFirmMutation,
  useLazySearchFirmsForDropdownQuery,
  useGetEmployeeTemplatesQuery,
  useLazyGetEmployeeTemplatesQuery,
  useCreateEmployeeTemplateMutation,
  useUpdateEmployeeTemplateMutation,
  useDeleteEmployeeTemplateMutation,
  useCreateFirmUserMutation,
  useGetFirmBillingSummaryQuery,
  useGetFirmConsumptionSummaryQuery,
  useDeleteFirmProductMutation,
  useGetCreateFirmProductOptionsQuery,
} from "./storageRoles/firmApi";

export {
  useGetFirmLogReportMutation,
  useGetFirmConsumentLogReportQuery,
} from "./storageRoles/firmLogApi";

export { useGetAuditLogsQuery } from "./storageRoles/auditApi";
export {
  useGetDevelopmentBoardQuery,
  useGetDevelopmentPullRequestsQuery,
  useRefreshDevelopmentBoardMutation,
  useRefreshDevelopmentPullRequestsMutation,
} from "./storageRoles/developmentApi";

export {
  useGetTaskProjectsQuery,
  useCreateTaskProjectMutation,
  useUpdateTaskProjectMutation,
  useArchiveTaskProjectMutation,
  useSetTaskProjectMembersMutation,
  useGetTaskLabelsQuery,
  useCreateTaskLabelMutation,
  useUpdateTaskLabelMutation,
  useDeleteTaskLabelMutation,
  useGetTaskAssigneesQuery,
  useGetTasksQuery,
  useGetTaskBoardQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useMoveTaskMutation,
  useWatchTaskMutation,
  useUnwatchTaskMutation,
  useDeleteTaskMutation,
  useArchiveTaskMutation,
  useUnarchiveTaskMutation,
  useAddTaskChecklistItemMutation,
  useUpdateTaskChecklistItemMutation,
  useToggleTaskChecklistItemMutation,
  useRemoveTaskChecklistItemMutation,
  useReorderTaskChecklistMutation,
  useGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useRequestTaskUploadMutation,
  useConfirmTaskUploadMutation,
  useLazyGetTaskDownloadUrlQuery,
  useDeleteTaskAttachmentMutation,
} from "./storageRoles/tasksApi";

export {
  notificationsApi,
  useGetNotificationsQuery,
  useGetNotificationsUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferencesMutation,
} from "./storageRoles/notificationsApi";

export {
  useCreateBillsMutation,
  useGetBillsQuery,
  useGetSalesSummaryQuery,
  useControllBillsMutation,
  useDeleteBillMutation,
} from "./storageRoles/billsApi";

export {
  useFetchProductInfoQuery,
  useAddProductInfoMutation,
  useUpdateProductInfoMutation,
  useDeleteProductInfoMutation,
} from "./storageRoles/productInfoApi";

export {
  useFetchProductTypeQuery,
  useAddProductTypeMutation,
  useUpdateProductTypeMutation,
  useDeleteProductTypeMutation,
} from "./storageRoles/productTypeApi";

export {
  useFetchCityQuery,
  useAddCityMutation,
  useUpdateCityMutation,
  useDeleteCityMutation,
} from "./storageRoles/cityApi";
export {
  useFetchTownQuery,
  useFetchTownWithyCityQuery,
  useAddTownMutation,
  useUpdateTownMutation,
  useDeleteTownMutation,
} from "./storageRoles/townApi";

export {
  useForgotPasswordMutation,
  useValidateResetPasswordTokenMutation,
  useResetPasswordMutation,
  useAcceptInvitationMutation,
  useCheckInvitationStatusMutation,
  useLoginMutation,
  useVerify2faMutation,
  useSendSecondaryCodeMutation,
  useVerifySecondaryCodeMutation,
  useLogoutQuery,
  useGetTrustedDevicesQuery,
  useUpdateTrustedDeviceLabelMutation,
  useRevokeTrustedDeviceMutation,
  useLazyGet2faSetupQuery,
  useConfirm2faMutation,
  useGet2faSetupWithTokenMutation,
  useComplete2faSetupMutation,
  useRegisterDeviceReplacingMutation,
  useVerifyAndStart2faChangeMutation,
  useSend2faChangeEmailCodeMutation,
  useVerify2faChangeByEmailCodeMutation,
  useConfirm2faChangeMutation,
  useSendPasswordChangeEmailCodeMutation,
  useUpdateMyPasswordMutation,
  useUpdateMeMutation,
} from "./authApi";

export {
  useFetchUserQuery,
  useLazyFetchUserQuery,
  useFetchPlayMakerQuery,
  useAddUserMutation,
  useGetUserQuery,
  useLazyGetUserActivityQuery,
  useLazyGetMyActivityQuery,
  useGetMyActivityDetailQuery,
  useLazyGetUserQuery,
  useUpdateUserMutation,
  useDeleteUserByIDMutation,
  useResendInvitationMutation,
  useGetAvailablePermissionsQuery,
  useUpdateUserPermissionsMutation,
  useGetEndUsersByFirmQuery,
  useLazyGetEndUsersByFirmQuery,
  useSearchUnlinkedEndUsersMutation,
  useLinkEndUserToFirmMutation,
  useUnlinkEndUserFromFirmMutation,
  useUpdateEndUserQuotaMutation,
  useLazySearchUsersQuery,
  useGetEndUserWalletQuery,
  useGrantEndUserOkkaliraMutation,
} from "./storageRoles/usersApi";

export {
  useLazyGetDealerRolesMyActivityQuery,
  useGetDealerRolesMyActivityDetailQuery,
} from "./dealerRoles/usersApi";

export {
  useGetDealerRolesFirmsQuery,
  useLazyGetDealerRolesFirmsQuery,
  useGetDealerRolesFirmByIDQuery,
  useLazyGetDealerRolesFirmByIDQuery,
  useGetDealerRolesBelowFirmsQuery,
  useGetDealerRolesCustomersByFirmIdQuery,
  useGetDealerRolesUsersByFirmIDQuery,
  useGetDealerRolesDeviceByFirmIDQuery,
  useLazyGetDealerRolesDeviceByFirmIDQuery,
  useGetDealerRolesFirmSyncQuery,
  useGetDealerRolesFirmProductsQuery,
  useLazyGetDealerRolesFirmProductsQuery,
  useGetDealerRolesFirmLogReportMutation,
  useGetDealerRolesFirmConsumentLogReportQuery,
  useGetDealerRolesRoleTemplatesQuery,
  useLazyGetDealerRolesRoleTemplatesQuery,
  useCreateDealerRolesRoleTemplateMutation,
  useUpdateDealerRolesRoleTemplateMutation,
  useDeleteDealerRolesRoleTemplateMutation,
  useSearchDealerUnlinkedFirmsMutation,
  useLinkDealerSubFirmMutation,
  useUnlinkDealerSubFirmMutation,
} from "./dealerRoles/firmApi";

export {
  useLazyGetDealerRolesDeviceDetailQuery,
  useLazyGetDealerRolesDeviceDistributionQuery,
  useGetDealerRolesDeviceConsumentStatsMutation,
  useGetDealerRolesDeviceConsumentLogsforReportMutation,
  useGetDealerRolesDeviceSettingLogsforReportMutation,
  useGetDealerRolesDeviceOperationLogsforReportMutation,
  useGetDealerRolesDeviceSettingLogsPaginatedMutation,
  useGetDealerRolesDeviceOperationLogsPaginatedMutation,
  useDealerUpdateDeviceInfoMutation,
  useDealerGiveDeviceQuotaMutation,
  useDealerTakeBackDeviceQuotaMutation,
} from "./dealerRoles/devicesApi";

export {
  useGetWalletTopupsQuery,
  useLazyGetWalletTopupsQuery,
  useGetWalletTransactionsQuery,
  useLazyGetWalletTransactionsQuery,
  useGetOkkaliraRateQuery,
  useUpdateOkkaliraRateMutation,
} from "./storageRoles/walletTopupApi";

export {
  useGetApkVersionsQuery,
  useLazyGetApkVersionsQuery,
  useGetApkCurrentVersionQuery,
  useGetPresignedUploadUrlMutation,
  useConfirmApkUploadMutation,
  useUpdateApkVersionMutation,
  useLazyGetApkDownloadUrlQuery,
} from "./storageRoles/apkApi";

export {
  useGetHardwareVersionsQuery,
  useLazyGetHardwareVersionsQuery,
  useGetHardwarePresignedUploadUrlMutation,
  useConfirmHardwareUploadMutation,
  useLazyGetHardwareDownloadUrlQuery,
  useDeleteHardwareVersionMutation,
} from "./storageRoles/hardwareApi";
export { useBroadcastPushMutation } from "./storageRoles/pushApi";

export {
  usePreviewUserAutoReportMutation,
  useSendUserAutoReportMutation,
  usePreviewFirmAutoReportMutation,
  useSendFirmAutoReportMutation,
  useSaveFirmAutoReportConfigMutation,
} from "./storageRoles/autoReportApi";

export {
  useDealerPreviewUserAutoReportMutation,
  useDealerSendUserAutoReportMutation,
  useDealerPreviewFirmAutoReportMutation,
  useDealerSendFirmAutoReportMutation,
  useDealerSaveFirmAutoReportConfigMutation,
} from "./dealerRoles/autoReportApi";

export {
  useGetPendingDevicesQuery,
  useAssignNewInstallMutation,
  useReplaceScreenMutation,
  useApproveMigrationMutation,
  useRejectPendingDeviceMutation,
} from "./storageRoles/pendingDevicesApi";

export {
  useLazyGetDeviceDetailQuery,
  useGetPaymentOperationLogsPaginatedQuery,
} from "./storageRoles/devicesSharedApi";

export {
  useGetPendingBootstrapRequestsQuery,
  useApproveBootstrapRequestMutation,
  useRejectBootstrapRequestMutation,
} from "./storageRoles/pendingBootstrapApi";

export {
  useGetEmqxClientsQuery,
  useLazyGetEmqxClientsQuery,
  useGetEmqxUsersQuery,
  useLazyGetEmqxUsersQuery,
  useAddEmqxUserMutation,
  useUpdateEmqxUserPasswordMutation,
  useDeleteEmqxUserMutation,
  useLazyGetDeviceMqttUserQuery,
  useGetUserAclQuery,
  useLazyGetUserAclQuery,
  useResetUserAclMutation,
  useApplyDeviceAclMutation,
} from "./storageRoles/emqxApi";

export {
  useGetFactoryFullCatalogQuery,
  useCreateFactoryLabelMutation,
  useUpdateFactoryLabelMutation,
  useDeleteFactoryLabelMutation,
  useDeleteFactoryPartMutation,
  useCreateFactoryPartMutation,
  useUpdateFactoryPartMutation,
  useCreateFactoryMachineTypeMutation,
  useUpdateFactoryMachineTypeMutation,
  useUpdateFactoryBomMutation,
  useUploadFactoryPartImageMutation,
  useDeleteFactoryPartImageMutation,
  useUploadFactoryMachineTypeImageMutation,
  useDeleteFactoryMachineTypeImageMutation,
  useGetFactoryOverviewQuery,
  useGetFactoryOperationsQuery,
  useCancelFactoryOperationLateMutation,
  useGetFactoryPartsQuery,
  useGetFactoryPartUnitsQuery,
  useGetFactoryPartUnitDetailQuery,
  useDeleteFactoryPartUnitMutation,
  useGetFactoryMachinesQuery,
  useGetFactoryBuildsQuery,
  useGetFactoryCatalogQuery,
  useGetFactoryZonesQuery,
  useCreateFactoryZoneMutation,
  useUpdateFactoryZoneMutation,
  useDeleteFactoryZoneMutation,
  useGetFactoryKiosksQuery,
  useGetFactoryLayoutItemsQuery,
  useCreateFactoryLayoutItemMutation,
  useUpdateFactoryLayoutItemMutation,
  useDeleteFactoryLayoutItemMutation,
  useGetPanelActivityQuery,
  useGetFactoryDocumentGroupsQuery,
  useGetFactoryDocumentsQuery,
  usePresignFactoryDocumentMutation,
  useCreateFactoryDocumentMutation,
  useUpdateFactoryDocumentMutation,
  useDeleteFactoryDocumentMutation,
} from "./storageRoles/factoryApi";
